import { ActionButton } from '../components/ActionButton';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import React from 'react';
import { IconButton } from '@material-ui/core';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Typography from '@material-ui/core/Typography';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import TabPanel from '@material-ui/lab/TabPanel';
import Box from '@material-ui/core/Box';
import { AppBar, makeStyles, Tab } from '@material-ui/core';

import { ShowSetting } from '../FormWidgets/ShowSliderSetting';
import { SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup';
import { LoadSettingsDialog } from '../Utils/load_settings';
import { EnableDisableSelect } from '../FormWidgets/Select.js';
import Desc from '../FormWidgets/Description';
import { HandleAction, isZoned } from '../Utils/utils.js';

import { useTabSettings } from './settingFunctions';
import { co2TemperatureSettings } from '../Utils/co2_utils';
import { SettingActions } from '../components';

const useStyles = makeStyles((theme) => ({
  lightTabsBar: {
    margin: '24px 24px 0',
    width: 'calc(100% - 48px)',
    [theme.breakpoints.down('xs')]: {
      margin: '10px 4px 0',
      minHeight: 48,
      width: 'calc(100% - 8px)'
    }
  }
}));

export default function LightsSettings({ ...other }) {
  const classes = useStyles();
  const {
    t,
    params,
    stateManager,
    controller,
    parent,
    values,
    setValues,
    controls,
    openLoadSettingsDialog,
    closeLoadSettingsDialog,
    tab_id
  } = useTabSettings();
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const read_access_climate = controller.access_rights.climate.read;
  const co2Temperature = co2TemperatureSettings(controller, parent);
  const write_access = controller.access_rights.lights.write;

  const callAction = (type, value) => {
    const zone_id = isZoned(controller) ? parent.id : undefined;
    HandleAction(stateManager.wsManager, controller, type, value, zone_id);
  };

  // Tab handling for lights Settings
  const subCnt = Math.max(1, Number(values?.lights_sub_zones_active_cnt ?? 1));
  const lightIds = Array.from({ length: subCnt }, (_, i) => i + 1); // [1..subCnt]
  const [activeLight, setActiveLight] = React.useState(lightIds[0]);
  React.useEffect(() => {
    if (!lightIds.includes(activeLight)) setActiveLight(lightIds[0]);
  }, [activeLight]);

  // Min of active lights_max values (LIGHTS 1..subCnt)
  const candidateMax = [
    values.lights_max,
    values.lights_max_2,
    values.lights_max_3,
    values.lights_max_4
  ]
    .slice(0, subCnt)
    .filter((v) => typeof v === 'number');
  const MinLightsMax = candidateMax.length ? Math.min(...candidateMax) : values.lights_max;

  return (
    <React.Fragment>
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
      />

      {write_access && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={controls.saveSettings}
        />
      )}

      {/* Tabbed LIGHTS */}
      {isZoned(controller) && (
        <SettingGroupGrid fullWidth={true}>
          <TabContext value={activeLight}>
            <AppBar position="static" color="default" className={classes.lightTabsBar}>
              <Box display="flex" justifyContent="center" width="100%">
                <TabList
                  variant="scrollable"
                  scrollButtons="auto"
                  onChange={(_, v) => setActiveLight(v)}
                >
                  {lightIds.map((lid, idx) => (
                    <Tab
                      key={lid}
                      value={lid}
                      label={`${t('lights')} ${idx + 1}`}
                      id={`light-tab-${idx}`}
                      aria-controls={`light-tabpanel-${idx}`}
                    />
                  ))}
                </TabList>
              </Box>
            </AppBar>

            {lightIds.map((lid, idx) => (
              <TabPanel key={lid} value={lid} id={`light-tabpanel-${idx}`} style={{ padding: 0 }}>
                <SettingGroupGrid controller={controller} id={'lights_multiple'}>
                  <ShowSetting
                    id={lid === 1 ? 'lights_max' : `lights_max_${lid}`}
                    show={'lights_max'}
                    controller={controller}
                    cur_value={lid === 1 ? values.lights_max : values[`lights_max_${lid}`]}
                    controller_value={
                      lid === 1
                        ? parent.settings.lights.lights_max
                        : parent.settings.lights[`lights_max_${lid}`]
                    }
                    min={values.lights_min}
                    controls={controls}
                    disabled={!write_access}
                  />
                </SettingGroupGrid>
              </TabPanel>
            ))}
          </TabContext>
        </SettingGroupGrid>
      )}

      <SettingGroupGrid controller={controller} id={'lights_single'}>
        {!isZoned(controller) && (
          <ShowSetting
            id="lights_max"
            controller={controller}
            cur_value={values.lights_max}
            controller_value={parent.settings.lights.lights_max}
            min={values.lights_min}
            controls={controls}
            disabled={!write_access}
          />
        )}
        <ShowSetting
          id="lights_min"
          controller={controller}
          cur_value={values.lights_min}
          controller_value={parent.settings.lights.lights_min}
          max={isZoned(controller) ? MinLightsMax : values.lights_max}
          controls={controls}
          disabled={!write_access}
        />
      </SettingGroupGrid>

      <SettingGroupGrid controller={controller} id={'lights_height_adjustment_hl'}>
        <ValueBox controller={controller} id={'lights_height_adjustment_single'}>
          <Typography gutterBottom>
            <Desc id="lights_height_adjustment_single" />
          </Typography>
          <div style={{ display: 'flex', gap: 21 }}>
            {[1, 2, 3, 4].map((num) => (
              <div
                key={num}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <ActionButton
                  title={connectionDisabledReason}
                  component={IconButton}
                  disabled={connectionStatus !== 'good'}
                  onClick={() => callAction(`action_lights_height_adjustment_up_${num}`)}
                >
                  <KeyboardArrowUpIcon />
                </ActionButton>
                <ActionButton
                  title={connectionDisabledReason}
                  component={IconButton}
                  disabled={connectionStatus !== 'good'}
                  onClick={() => callAction(`action_lights_height_adjustment_down_${num}`)}
                >
                  <KeyboardArrowDownIcon />
                </ActionButton>
                <Typography variant="caption" color="Primary">
                  <Desc id={`${num}`} />
                </Typography>
              </div>
            ))}
          </div>
        </ValueBox>
        <ValueBox controller={controller} id={'lights_height_adjustment_global'}>
          <Typography gutterBottom>
            <Desc id="lights_height_adjustment_global" />
          </Typography>
          <ActionButton
            title={connectionDisabledReason}
            component={IconButton}
            disabled={connectionStatus !== 'good'}
            onClick={() => callAction('action_lights_height_adjustment_up_0')}
          >
            <KeyboardArrowUpIcon />
          </ActionButton>
          <br />
          <ActionButton
            title={connectionDisabledReason}
            component={IconButton}
            disabled={connectionStatus !== 'good'}
            onClick={() => callAction('action_lights_height_adjustment_down_0')}
          >
            <KeyboardArrowDownIcon />
          </ActionButton>
        </ValueBox>
      </SettingGroupGrid>

      <SettingGroupGrid controller={controller} id={'lights_sunrise_duration'}>
        <ShowSetting
          id="light_sunrise_duration"
          controller={controller}
          cur_value={values.light_sunrise_duration}
          controller_value={parent.settings.lights.light_sunrise_duration}
          controls={controls}
          disabled={!write_access}
        />
      </SettingGroupGrid>

      <SettingGroupGrid controller={controller} id={'over_temperature_hl'}>
        <ValueBox controller={controller} id={'lights_dim_over_temp_enable'}>
          <Typography gutterBottom>
            <Desc id="lights_dim_over_temp_enable" />
          </Typography>
          <EnableDisableSelect
            value={values.lights_dim_over_temp_enable}
            controller_value={parent.settings.lights.lights_dim_over_temp_enable}
            name="lights_dim_over_temp_enable"
            onChange={(event, value) => {
              controls.setValue('lights_dim_over_temp_enable', event.target.value);
            }}
            disabled={!write_access}
          />
        </ValueBox>
        <ShowSetting
          id="lights_dim_over_temp_temp_day"
          controller={controller}
          cur_value={values.lights_dim_over_temp_temp_day}
          controller_value={parent.settings.lights.lights_dim_over_temp_temp_day}
          min={read_access_climate ? parent.settings.climate.temp_sp_day / 10 + 1 : 0}
          controls={controls}
          disabled={!write_access}
          type="float"
        />
        <ShowSetting
          id="lights_dim_over_temp_temp_co2"
          controller={controller}
          cur_value={values.lights_dim_over_temp_temp_co2}
          controller_value={parent.settings.lights.lights_dim_over_temp_temp_co2}
          min={co2Temperature.readable ? co2Temperature.value / 10 + 1 : 0}
          controls={controls}
          disabled={!write_access}
          type="float"
        />
        <ShowSetting
          id="lights_dim_over_temp_reduct_ph1"
          controller={controller}
          cur_value={values.lights_dim_over_temp_reduct_ph1}
          controller_value={parent.settings.lights.lights_dim_over_temp_reduct_ph1}
          min={values.lights_min - 1}
          max={MinLightsMax}
          controls={controls}
          disabled={!write_access}
          off_at={values.lights_min - 1}
        />
        <ShowSetting
          id="lights_dim_over_temp_reduct_ph2"
          controller={controller}
          cur_value={values.lights_dim_over_temp_reduct_ph2}
          controller_value={parent.settings.lights.lights_dim_over_temp_reduct_ph2}
          min={values.lights_min - 1}
          max={values.lights_dim_over_temp_reduct_ph1}
          controls={controls}
          disabled={!write_access}
          off_at={values.lights_min - 1}
        />
        <ShowSetting
          id="lights_dim_over_temp_reduct_ph3"
          controller={controller}
          cur_value={values.lights_dim_over_temp_reduct_ph3}
          controller_value={parent.settings.lights.lights_dim_over_temp_reduct_ph3}
          min={values.lights_min - 1}
          max={values.lights_dim_over_temp_reduct_ph2}
          controls={controls}
          disabled={!write_access}
          off_at={values.lights_min - 1}
        />
        <ShowSetting
          id="lights_dim_over_temp_reduct_wait_minute"
          controller={controller}
          cur_value={values.lights_dim_over_temp_reduct_wait_minute}
          controller_value={parent.settings.lights.lights_dim_over_temp_reduct_wait_minute}
          controls={controls}
          disabled={!write_access}
        />
      </SettingGroupGrid>

      {write_access && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={controls.saveSettings}
        />
      )}
    </React.Fragment>
  );
}
